import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Container, Grid, makeStyles } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import HelpRounded from '@material-ui/icons/HelpRounded'
import Tooltip from '@material-ui/core/Tooltip'

import { isEmpty } from 'lodash'
import behaviourChartConst from '../data/behaviorAnalysisChart'
import PageSection from '../components/PageSection'
import AreaChart from '../components/AreaChart'
import ProjectionChart from '../components/ProjectionChart'
import ChartDialog from '../components/ChartDialog'
import { Context as PortfolioContext } from '../contexts/portfolio'
import RadioToggle from '../components/RadioToggle'
import ChartTooltipModal from '../components/ChartTooltipModal'

const useStyles = makeStyles({
  subheading: { marginBottom: '10px' },
  chart: {
    width: '100%',
    height: '300px',
    padding: '0px !important',
    flexBasis: 'unset',
  },
  lineChart: {
    marginBottom: 30,
  },
  marketContextContainer: {
    background: '#fff',
    maxWidth: '100%',
    paddingLeft: 0,
    paddingRight: 0,
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    '& > div': {
      backgroundColor: '#F3F3F3',
      padding: '8px 12px',
      marginRight: 10,
      borderRadius: 20,
    },
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    marginTop: -20,
    lineHeight: '18px',
    fontWeight: 400,
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      marginRight: 16,
      '& div': {
        marginRight: 8,
        width: 10,
        height: 10,
        borderRadius: '50%',
      },
    },
  },
  customGrid: {
    margin: 'unset',
    width: '100% !important',
    justifyContent: 'space-between',
  },
  headChartSection: {
    marginBottom: 12,
    width: '100%',
  },
  titleCombo: {
    width: '100%',
  },
  icon: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#757575',
    '& svg': {
      width: 14,
    },
    marginTop: 10,
  },
  radioToggle: {},
  expandIcon: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#474747',
    padding: '0px 8px',
    '& svg': {
      width: 20,
    },
  },
  title: {
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 600,
    marginRight: 8,
    color: '#474747',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  chartChild: {
    width: '100%',
    height: 250,
  },
  chartHeader: {
    color: '#474747',
    marginBottom: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headIntro: {
    width: '100%',
  },
  subTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: '#474747',
  },
  toolTip:  {
    color: '#8D9CB1 !important',
    margin: 'auto',
    fontSize: '1.3rem',
    cursor: 'pointer',
    marginLeft: '5px',
  }
})

const CustomToolTip = withStyles({
  tooltip: {
    backgroundColor: 'transparent !important',
    boxShadow: 'unset !important',
  },
})(Tooltip)

const ESTIMATE_INDEX_TOGGLE = [
  { id: 0, value: 'Annual' },
  { id: 2, value: 'Interim' },
]

const BehaviouralAnalysisMobile = ({ tickerId }) => {
  const classes = useStyles()
  const { state, setShowBehavioural } = useContext(PortfolioContext)
  const [isOpenExpandModal, setOpenExpandModal] = useState(false)
  const [chartDetail, setChartDetail] = React.useState({})
  const [behaviourChart, setBehaviourChart] = useState(behaviourChartConst)
  const [estimateChart, setEstimateChart] = useState(behaviourChartConst[0])
  const [openTooltip, setOpenTooltip] = React.useState(false)
  const [tooltipInfo, setTooltipInfo] = React.useState({})
  const [toggleEstimateIndexChart, setToggleEstimateIndexChart] = useState(
    ESTIMATE_INDEX_TOGGLE[0],
  )
  const [frequency, setFrequency] = useState('annual')

  useEffect(() => {
    setBehaviourChart(charts =>
      ['estimates', 'highConvictions', 'bondInvestors'].map((k, i) => ({
        ...charts[i],
        data: state.behaviorData[k],
      })),
    )
    if (
      state.behaviorData['estimates']?.filter(d => d.frequency !== 'annual')
        .length > 0 &&
      state.behaviorData['estimates']?.filter(d => d.frequency === 'annual')
        .length === 0
    ) {
      onToggleEstimateIndexChart({ id: 2, value: 'Interim' })
    }
  }, [state.behaviorData])

  const handleClickOpenTooltip = chart => {
    setTooltipInfo(chart.tooltipInfo)
    setOpenTooltip(true)
  }

  const onToggleEstimateIndexChart = obj => {
    setToggleEstimateIndexChart(obj)
    setFrequency(obj.value.toLowerCase())
  }

  const chartDiv = useMemo(
    () => (chart, inModal, estimateChartFrequency) => {
      return !inModal ? (
        chart?.data?.length > 1 && (
          <Grid
            key={chart.title}
            className={classes.chart}
            item
            xs={12}
            style={{
              marginTop: chart.title === 'Equity Investor Confidence' ? 30 : 10,
            }}
          >
            <ChartSection
              subTitle={chart.subTitle}
              title={chart.title}
              chartDetail={chart}
            >
              {chart.type === 'ProjectionChart' ? (
                <>
                  <ProjectionChart
                    estimateChartFrequency={estimateChartFrequency}
                    inModal={inModal}
                    data={chart.data}
                  />
                </>
              ) : (
                <AreaChart data={chart.data} yKey={[chart.yKey]} />
              )}
            </ChartSection>
          </Grid>
        )
      ) : (
        <ChartSection
          title={chart.title}
          subTitle={chart.subTitle}
          inModal={inModal}
        >
          {chart.type === 'ProjectionChart' ? (
            <>
              <ProjectionChart
                estimateChartFrequency={estimateChartFrequency}
                inModal={inModal}
                data={chart.data}
              />
              <div className={classes.legend}>
                <div>
                  <div style={{ backgroundColor: '#3393F3' }} />
                  Smart Consensus
                </div>
                <div>
                  <div style={{ backgroundColor: '#34D8E2' }} />
                  Consensus
                </div>
              </div>
            </>
          ) : (
            <AreaChart data={chart.data} yKey={[chart.yKey]} />
          )}
        </ChartSection>
      )
    },
    [state.behaviorData],
  )

  const showingToggle =
    state.behaviorData['estimates']?.filter(d => d.frequency !== 'annual')
      .length > 0 &&
    state.behaviorData['estimates']?.filter(d => d.frequency === 'annual')
      .length > 0

  const ChartSection = ({
    title,
    subTitle,
    inModal,
    children,
    chartDetail,
  }) => {
    return (
      <>
        <div className={classes.headChartSection}>
          <div className={classes.headIntro}>
            <div className={classes.titleCombo}>
              <div className={classes.title}>
                {title}
                <HelpRounded
                  className={classes.toolTip}
                  onClick={() => handleClickOpenTooltip(chartDetail)}
                />
              </div>
              <div className={classes.subTitle}>
                ({subTitle})
                <div className={classes.icon}>
                  {title === 'Forward Looking EPS Forecast' &&
                    showingToggle &&
                    state.behaviorData['estimates'].length > 0 && (
                      <div className={classes.radioToggle}>
                        <RadioToggle
                          list={ESTIMATE_INDEX_TOGGLE}
                          getActiveValue={value =>
                            onToggleEstimateIndexChart(value)
                          }
                          activeItemDefault={toggleEstimateIndexChart}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.chartChild}>
          {children}
          {title === 'Forward Looking EPS Forecast' && (
            <div className={classes.legend}>
              <div>
                <div style={{ backgroundColor: '#3393F3' }} />
                Smart Consensus
              </div>
              <div>
                <div style={{ backgroundColor: '#34D8E2' }} />
                Consensus
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  if (!behaviourChart) {
    return null
  }

  return (
    <>
      <PageSection
        title="Behavioural Analysis"
        subTitle="Street Perception & Investor Confidence Measures"
        behaviouralToolTip
      >
        <ChartDialog
          chart={chartDiv(chartDetail, true, frequency)}
          isOpenExpandModal={isOpenExpandModal}
          setOpenExpandModal={setOpenExpandModal}
        />
        <Container className={classes.marketContextContainer}>
          <Grid container spacing={10} className={classes.customGrid}>
            {behaviourChart.map(chart => chartDiv(chart, false, frequency))}
          </Grid>
        </Container>
      </PageSection>
      <ChartTooltipModal
        open={openTooltip}
        handleClose={() => {
          setOpenTooltip(false)
        }}
        tooltipInfo={tooltipInfo}
      />
    </>
  )
}

export default React.memo(BehaviouralAnalysisMobile)
